import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { useScrollReveal } from "../hooks/use-scroll-reveal";
import {
  ArrowRight,
  MessageSquare,
  ClipboardList,
  Palette,
  Paintbrush,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

const processSteps = [
  {
    id: 1,
    icon: MessageSquare,
    title: "Free Consultation",
    description:
      "We discuss your vision, color preferences, timeline, and provide a detailed walkthrough of your space.",
    duration: "Same Day",
  },
  {
    id: 2,
    icon: ClipboardList,
    title: "Detailed Estimate",
    description:
      "Receive a comprehensive written estimate with no hidden fees. We'll explain every aspect of the job.",
    duration: "24-48 Hours",
  },
  {
    id: 3,
    icon: Palette,
    title: "Color Selection",
    description:
      "Our color consultants help you choose the perfect palette. We provide samples and test patches.",
    duration: "Included",
  },
  {
    id: 4,
    icon: Paintbrush,
    title: "Surface Preparation",
    description:
      "Thorough prep work including cleaning, sanding, patching, priming, and protecting your belongings.",
    duration: "Project Start",
  },
  {
    id: 5,
    icon: Sparkles,
    title: "Professional Painting",
    description:
      "Expert application using premium paints and techniques for a flawless, long-lasting finish.",
    duration: "1-5 Days",
  },
  {
    id: 6,
    icon: CheckCircle2,
    title: "Final Walkthrough",
    description:
      "Complete inspection with you, touch-ups as needed, and cleanup. Your satisfaction is guaranteed.",
    duration: "Same Day",
  },
];

export const Process = ({ showAll = false }) => {
  const displayedSteps = showAll ? processSteps : processSteps;
  const [gridRef, gridVisible] = useScrollReveal();

  return (
    <section
      data-testid="process-section"
      className="py-20 sm:py-28 bg-white relative overflow-hidden"
    >
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0a1628_1px,transparent_1px),linear-gradient(to_bottom,#0a1628_1px,transparent_1px)] bg-[size:5rem_5rem]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 sm:mb-16">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
            How We Work
          </span>
          <h2
            data-testid="process-heading"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
          >
            Our Process
          </h2>
          <p className="font-body text-sm sm:text-base text-brand-slate mt-3 max-w-xl mx-auto leading-relaxed">
            A simple, transparent approach to transform your home with professional painting.
          </p>
        </div>

        {/* Cards */}
        <div
          ref={gridRef}
          className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 reveal-stagger ${gridVisible ? 'visible' : ''}`}
        >
          {displayedSteps.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                data-testid={`process-step-${step.id}`}
                className="group relative bg-brand-ice border border-border rounded-xl p-6 transition-all duration-400 card-lift"
              >
                {/* Step number watermark */}
                <span className="absolute top-4 right-5 font-heading text-5xl font-semibold text-brand-navy/[0.06] leading-none select-none transition-colors duration-400">
                  {String(step.id).padStart(2, "0")}
                </span>

                {/* Icon */}
                <div className="w-11 h-11 bg-brand-orange/10 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-brand-orange" />
                </div>

                <h3 className="font-heading text-base sm:text-lg font-semibold uppercase text-brand-navy mb-1.5 transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-brand-slate text-sm leading-relaxed mb-3 transition-colors duration-300">
                  {step.description}
                </p>

                {/* Duration */}
                <span className="inline-flex items-center text-[11px] font-medium uppercase tracking-wider text-brand-orange bg-brand-orange/10 px-2.5 py-1 rounded-full">
                  {step.duration}
                </span>

                {/* Bottom accent */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange rounded-b-xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left" />
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 sm:mt-14">
          {showAll ? (
            <Link to="/quote">
              <Button
                data-testid="start-project-btn"
                size="lg"
                className="bg-brand-orange text-white font-heading font-semibold uppercase tracking-wide hover:bg-[#d9432a] transition-colors duration-300"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          ) : (
            <Link to="/process">
              <Button
                data-testid="view-full-process-btn"
                variant="outline"
                size="lg"
                className="border border-white/20 text-white font-heading font-semibold uppercase tracking-wide hover:bg-white hover:text-brand-navy transition-all duration-300"
              >
                Learn More
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
